/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {ApplicationRef} from '../application_ref';
import {ChangeDetectorRef, ChangeDetectorStatus} from '../change_detection/change_detection';
import {Injector, THROW_IF_NOT_FOUND} from '../di/injector';
import {isPresent} from '../facade/lang';
import {WtfScopeFn, wtfCreateScope, wtfLeave} from '../profile/profile';
import {DirectRenderer, RenderComponentType, RenderDebugInfo, Renderer} from '../render/api';

import {AnimationViewContext} from './animation_view_context';
import {ComponentRef} from './component_factory';
import {DebugContext, StaticNodeDebugInfo} from './debug_context';
import {ElementInjector} from './element_injector';
import {ExpressionChangedAfterItHasBeenCheckedError, ViewDestroyedError, ViewWrappedError} from './errors';
import {ViewContainer} from './view_container';
import {ViewRef_} from './view_ref';
import {ViewType} from './view_type';
import {ViewUtils, addToArray} from './view_utils';

const /** @type {?} */ _scope_check: WtfScopeFn = wtfCreateScope(`AppView#check(ascii id)`);

/**
 * @experimental
 */
const /** @type {?} */ EMPTY_CONTEXT = new Object();

const /** @type {?} */ UNDEFINED = new Object();
/**
 *  Cost of making objects: http://jsperf.com/instantiate-size-of-object
  * *
 * @abstract
 */
export abstract class AppView<T> {
  ref: ViewRef_<T>;
  lastRootNode: any;
  allNodes: any[];
  disposables: Function[];
  viewContainer: ViewContainer;
  // This will be set if a view is directly attached to an ApplicationRef
  // and not to a view container.
  appRef: ApplicationRef;

  numberOfChecks: number = 0;

  renderer: Renderer;
private _hasExternalHostElement: boolean;
private _hostInjector: Injector;
private _hostProjectableNodes: any[][];
private _animationContext: AnimationViewContext;
private _directRenderer: DirectRenderer;
public context: T;
/**
 * @param {?} clazz
 * @param {?} componentType
 * @param {?} type
 * @param {?} viewUtils
 * @param {?} parentView
 * @param {?} parentIndex
 * @param {?} parentElement
 * @param {?} cdMode
 * @param {?=} declaredViewContainer
 */
constructor(
public clazz: any,
public componentType: RenderComponentType,
public type: ViewType,
public viewUtils: ViewUtils,
public parentView: AppView<any>,
public parentIndex: number,
public parentElement: any,
public cdMode: ChangeDetectorStatus,
public declaredViewContainer: ViewContainer = null) {
    this.ref = new ViewRef_(this, viewUtils.animationQueue);
    if (type === ViewType.COMPONENT || type === ViewType.HOST) {
      this.renderer = viewUtils.renderComponent(componentType);
    } else {
      this.renderer = parentView.renderer;
    }
    this._directRenderer = (this.renderer as any).directRenderer;
  }
/**
 * @return {?}
 */
get animationContext(): AnimationViewContext {
    if (!this._animationContext) {
      this._animationContext = new AnimationViewContext(this.viewUtils.animationQueue);
    }
    return this._animationContext;
  }
/**
 * @return {?}
 */
get destroyed(): boolean { return this.cdMode === ChangeDetectorStatus.Destroyed; }
/**
 * @param {?} context
 * @return {?}
 */
create(context: T) {
    this.context = context;
    return this.createInternal(null);
  }
/**
 * @param {?} rootSelectorOrNode
 * @param {?} hostInjector
 * @param {?} projectableNodes
 * @return {?}
 */
createHostView(rootSelectorOrNode: string|any, hostInjector: Injector, projectableNodes: any[][]):
      ComponentRef<any> {
    this.context = /** @type {?} */(( <any>EMPTY_CONTEXT));
    this._hasExternalHostElement = isPresent(rootSelectorOrNode);
    this._hostInjector = hostInjector;
    this._hostProjectableNodes = projectableNodes;
    return this.createInternal(rootSelectorOrNode);
  }
/**
 *  Overwritten by implementations.
  * Returns the ComponentRef for the host element for ViewType.HOST.
 * @param {?} rootSelectorOrNode
 * @return {?}
 */
createInternal(rootSelectorOrNode: string|any): ComponentRef<any> { return null; }
/**
 *  Overwritten by implementations.
 * @param {?} templateNodeIndex
 * @return {?}
 */
createEmbeddedViewInternal(templateNodeIndex: number): AppView<any> { return null; }
/**
 * @param {?} lastRootNode
 * @param {?} allNodes
 * @param {?} disposables
 * @return {?}
 */
init(lastRootNode: any, allNodes: any[], disposables: Function[]) {
    this.lastRootNode = lastRootNode;
    this.allNodes = allNodes;
    this.disposables = disposables;
    if (this.type === ViewType.COMPONENT) {
      this.dirtyParentQueriesInternal();
    }
  }
/**
 * @param {?} token
 * @param {?} nodeIndex
 * @param {?=} notFoundValue
 * @return {?}
 */
injectorGet(token: any, nodeIndex: number, notFoundValue: any = THROW_IF_NOT_FOUND): any {
    let /** @type {?} */ result = UNDEFINED;
    let /** @type {?} */ view: AppView<any> = this;
    while (result === UNDEFINED) {
      if (isPresent(nodeIndex)) {
        result = view.injectorGetInternal(token, nodeIndex, UNDEFINED);
      }
      if (result === UNDEFINED && view.type === ViewType.HOST) {
        result = view._hostInjector.get(token, notFoundValue);
      }
      nodeIndex = view.parentIndex;
      view = view.parentView;
    }
    return result;
  }
/**
 *  Overwritten by implementations
 * @param {?} token
 * @param {?} nodeIndex
 * @param {?} notFoundResult
 * @return {?}
 */
injectorGetInternal(token: any, nodeIndex: number, notFoundResult: any): any {
    return notFoundResult;
  }
/**
 * @param {?} nodeIndex
 * @return {?}
 */
injector(nodeIndex: number): Injector { return new ElementInjector(this, nodeIndex); }
/**
 * @return {?}
 */
detachAndDestroy() {
    if (this.viewContainer) {
      this.viewContainer.detachView(this.viewContainer.nestedViews.indexOf(this));
    } else if (this.appRef) {
      this.appRef.detachView(this.ref);
    } else if (this._hasExternalHostElement) {
      this.detach();
    }
    this.destroy();
  }
/**
 * @return {?}
 */
destroy() {
    if (this.cdMode === ChangeDetectorStatus.Destroyed) {
      return;
    }
    const /** @type {?} */ hostElement = this.type === ViewType.COMPONENT ? this.parentElement : null;
    if (this.disposables) {
      for (let /** @type {?} */ i = 0; i < this.disposables.length; i++) {
        this.disposables[i]();
      }
    }
    this.destroyInternal();
    this.dirtyParentQueriesInternal();

    if (this._animationContext) {
      this._animationContext.onAllActiveAnimationsDone(
          () => this.renderer.destroyView(hostElement, this.allNodes));
    } else {
      this.renderer.destroyView(hostElement, this.allNodes);
    }

    this.cdMode = ChangeDetectorStatus.Destroyed;
  }
/**
 *  Overwritten by implementations
 * @return {?}
 */
destroyInternal(): void {}
/**
 *  Overwritten by implementations
 * @return {?}
 */
detachInternal(): void {}
/**
 * @return {?}
 */
detach(): void {
    this.detachInternal();
    if (this._animationContext) {
      this._animationContext.onAllActiveAnimationsDone(() => this._renderDetach());
    } else {
      this._renderDetach();
    }
    if (this.declaredViewContainer && this.declaredViewContainer !== this.viewContainer &&
        this.declaredViewContainer.projectedViews) {
      const /** @type {?} */ projectedViews = this.declaredViewContainer.projectedViews;
      const /** @type {?} */ index = projectedViews.indexOf(this);
      // perf: pop is faster than splice!
      if (index >= projectedViews.length - 1) {
        projectedViews.pop();
      } else {
        projectedViews.splice(index, 1);
      }
    }
    this.appRef = null;
    this.viewContainer = null;
    this.dirtyParentQueriesInternal();
  }
/**
 * @return {?}
 */
private _renderDetach() {
    if (this._directRenderer) {
      this.visitRootNodesInternal(this._directRenderer.remove, null);
    } else {
      this.renderer.detachView(this.flatRootNodes);
    }
  }
/**
 * @param {?} appRef
 * @return {?}
 */
attachToAppRef(appRef: ApplicationRef) {
    if (this.viewContainer) {
      throw new Error('This view is already attached to a ViewContainer!');
    }
    this.appRef = appRef;
    this.dirtyParentQueriesInternal();
  }
/**
 * @param {?} viewContainer
 * @param {?} prevView
 * @return {?}
 */
attachAfter(viewContainer: ViewContainer, prevView: AppView<any>) {
    if (this.appRef) {
      throw new Error('This view is already attached directly to the ApplicationRef!');
    }
    this._renderAttach(viewContainer, prevView);
    this.viewContainer = viewContainer;
    if (this.declaredViewContainer && this.declaredViewContainer !== viewContainer) {
      if (!this.declaredViewContainer.projectedViews) {
        this.declaredViewContainer.projectedViews = [];
      }
      this.declaredViewContainer.projectedViews.push(this);
    }
    this.dirtyParentQueriesInternal();
  }
/**
 * @param {?} viewContainer
 * @param {?} prevView
 * @return {?}
 */
moveAfter(viewContainer: ViewContainer, prevView: AppView<any>) {
    this._renderAttach(viewContainer, prevView);
    this.dirtyParentQueriesInternal();
  }
/**
 * @param {?} viewContainer
 * @param {?} prevView
 * @return {?}
 */
private _renderAttach(viewContainer: ViewContainer, prevView: AppView<any>) {
    const /** @type {?} */ prevNode = prevView ? prevView.lastRootNode : viewContainer.nativeElement;
    if (this._directRenderer) {
      const /** @type {?} */ nextSibling = this._directRenderer.nextSibling(prevNode);
      if (nextSibling) {
        this.visitRootNodesInternal(this._directRenderer.insertBefore, nextSibling);
      } else {
        const /** @type {?} */ parentElement = this._directRenderer.parentElement(prevNode);
        if (parentElement) {
          this.visitRootNodesInternal(this._directRenderer.appendChild, parentElement);
        }
      }
    } else {
      this.renderer.attachViewAfter(prevNode, this.flatRootNodes);
    }
  }
/**
 * @return {?}
 */
get changeDetectorRef(): ChangeDetectorRef { return this.ref; }
/**
 * @return {?}
 */
get flatRootNodes(): any[] {
    const /** @type {?} */ nodes: any[] = [];
    this.visitRootNodesInternal(addToArray, nodes);
    return nodes;
  }
/**
 * @param {?} parentElement
 * @param {?} ngContentIndex
 * @return {?}
 */
projectNodes(parentElement: any, ngContentIndex: number) {
    if (this._directRenderer) {
      this.visitProjectedNodes(ngContentIndex, this._directRenderer.appendChild, parentElement);
    } else {
      const /** @type {?} */ nodes: any[] = [];
      this.visitProjectedNodes(ngContentIndex, addToArray, nodes);
      this.renderer.projectNodes(parentElement, nodes);
    }
  }
/**
 * @param {?} ngContentIndex
 * @param {?} cb
 * @param {?} c
 * @return {?}
 */
visitProjectedNodes<C>(ngContentIndex: number, cb: (node: any, ctx: C) => void, c: C): void {
    switch (this.type) {
      case ViewType.EMBEDDED:
        this.parentView.visitProjectedNodes(ngContentIndex, cb, c);
        break;
      case ViewType.COMPONENT:
        if (this.parentView.type === ViewType.HOST) {
          const /** @type {?} */ nodes = this.parentView._hostProjectableNodes[ngContentIndex] || [];
          for (let /** @type {?} */ i = 0; i < nodes.length; i++) {
            cb(nodes[i], c);
          }
        } else {
          this.parentView.visitProjectableNodesInternal(this.parentIndex, ngContentIndex, cb, c);
        }
        break;
    }
  }
/**
 *  Overwritten by implementations
 * @param {?} cb
 * @param {?} c
 * @return {?}
 */
visitRootNodesInternal<C>(cb: (node: any, ctx: C) => void, c: C): void {}
/**
 *  Overwritten by implementations
 * @param {?} nodeIndex
 * @param {?} ngContentIndex
 * @param {?} cb
 * @param {?} c
 * @return {?}
 */
visitProjectableNodesInternal<C>(
      nodeIndex: number, ngContentIndex: number, cb: (node: any, ctx: C) => void, c: C): void {}
/**
 *  Overwritten by implementations
 * @return {?}
 */
dirtyParentQueriesInternal(): void {}
/**
 * @param {?} throwOnChange
 * @return {?}
 */
internalDetectChanges(throwOnChange: boolean): void {
    if (this.cdMode !== ChangeDetectorStatus.Detached) {
      this.detectChanges(throwOnChange);
    }
  }
/**
 * @param {?} throwOnChange
 * @return {?}
 */
detectChanges(throwOnChange: boolean): void {
    const /** @type {?} */ s = _scope_check(this.clazz);
    if (this.cdMode === ChangeDetectorStatus.Checked ||
        this.cdMode === ChangeDetectorStatus.Errored)
      return;
    if (this.cdMode === ChangeDetectorStatus.Destroyed) {
      this.throwDestroyedError('detectChanges');
    }
    this.detectChangesInternal(throwOnChange);
    if (this.cdMode === ChangeDetectorStatus.CheckOnce) this.cdMode = ChangeDetectorStatus.Checked;

    this.numberOfChecks++;
    wtfLeave(s);
  }
/**
 *  Overwritten by implementations
 * @param {?} throwOnChange
 * @return {?}
 */
detectChangesInternal(throwOnChange: boolean): void {}
/**
 * @return {?}
 */
markAsCheckOnce(): void { this.cdMode = ChangeDetectorStatus.CheckOnce; }
/**
 * @return {?}
 */
markPathToRootAsCheckOnce(): void {
    let /** @type {?} */ c: AppView<any> = this;
    while (isPresent(c) && c.cdMode !== ChangeDetectorStatus.Detached) {
      if (c.cdMode === ChangeDetectorStatus.Checked) {
        c.cdMode = ChangeDetectorStatus.CheckOnce;
      }
      if (c.type === ViewType.COMPONENT) {
        c = c.parentView;
      } else {
        c = c.viewContainer ? c.viewContainer.parentView : null;
      }
    }
  }
/**
 * @param {?} cb
 * @return {?}
 */
eventHandler<E, R>(cb: (eventName: string, event?: E) => R): (eventName: string, event?: E) => R {
    return cb;
  }
/**
 * @param {?} details
 * @return {?}
 */
throwDestroyedError(details: string): void { throw new ViewDestroyedError(details); }
}

function AppView_tsickle_Closure_declarations() {
 /** @type {?} */
AppView.prototype.ref;
 /** @type {?} */
AppView.prototype.lastRootNode;
 /** @type {?} */
AppView.prototype.allNodes;
 /** @type {?} */
AppView.prototype.disposables;
 /** @type {?} */
AppView.prototype.viewContainer;
 /** @type {?} */
AppView.prototype.appRef;
 /** @type {?} */
AppView.prototype.numberOfChecks;
 /** @type {?} */
AppView.prototype.renderer;
 /** @type {?} */
AppView.prototype._hasExternalHostElement;
 /** @type {?} */
AppView.prototype._hostInjector;
 /** @type {?} */
AppView.prototype._hostProjectableNodes;
 /** @type {?} */
AppView.prototype._animationContext;
 /** @type {?} */
AppView.prototype._directRenderer;
 /** @type {?} */
AppView.prototype.context;
 /** @type {?} */
AppView.prototype.clazz;
 /** @type {?} */
AppView.prototype.componentType;
 /** @type {?} */
AppView.prototype.type;
 /** @type {?} */
AppView.prototype.viewUtils;
 /** @type {?} */
AppView.prototype.parentView;
 /** @type {?} */
AppView.prototype.parentIndex;
 /** @type {?} */
AppView.prototype.parentElement;
 /** @type {?} */
AppView.prototype.cdMode;
 /** @type {?} */
AppView.prototype.declaredViewContainer;
}


export class DebugAppView<T> extends AppView<T> {
private _currentDebugContext: DebugContext = null;
/**
 * @param {?} clazz
 * @param {?} componentType
 * @param {?} type
 * @param {?} viewUtils
 * @param {?} parentView
 * @param {?} parentIndex
 * @param {?} parentNode
 * @param {?} cdMode
 * @param {?} staticNodeDebugInfos
 * @param {?=} declaredViewContainer
 */
constructor(
      clazz: any, componentType: RenderComponentType, type: ViewType, viewUtils: ViewUtils,
      parentView: AppView<any>, parentIndex: number, parentNode: any, cdMode: ChangeDetectorStatus,
public staticNodeDebugInfos: StaticNodeDebugInfo[],
      declaredViewContainer: ViewContainer = null) {
    super(
        clazz, componentType, type, viewUtils, parentView, parentIndex, parentNode, cdMode,
        declaredViewContainer);
  }
/**
 * @param {?} context
 * @return {?}
 */
create(context: T) {
    this._resetDebug();
    try {
      return super.create(context);
    } catch ( /** @type {?} */e) {
      this._rethrowWithContext(e);
      throw e;
    }
  }
/**
 * @param {?} rootSelectorOrNode
 * @param {?} injector
 * @param {?=} projectableNodes
 * @return {?}
 */
createHostView(
      rootSelectorOrNode: string|any, injector: Injector,
      projectableNodes: any[][] = null): ComponentRef<any> {
    this._resetDebug();
    try {
      return super.createHostView(rootSelectorOrNode, injector, projectableNodes);
    } catch ( /** @type {?} */e) {
      this._rethrowWithContext(e);
      throw e;
    }
  }
/**
 * @param {?} token
 * @param {?} nodeIndex
 * @param {?=} notFoundResult
 * @return {?}
 */
injectorGet(token: any, nodeIndex: number, notFoundResult?: any): any {
    this._resetDebug();
    try {
      return super.injectorGet(token, nodeIndex, notFoundResult);
    } catch ( /** @type {?} */e) {
      this._rethrowWithContext(e);
      throw e;
    }
  }
/**
 * @return {?}
 */
detach(): void {
    this._resetDebug();
    try {
      super.detach();
    } catch ( /** @type {?} */e) {
      this._rethrowWithContext(e);
      throw e;
    }
  }
/**
 * @return {?}
 */
destroy() {
    this._resetDebug();
    try {
      super.destroy();
    } catch ( /** @type {?} */e) {
      this._rethrowWithContext(e);
      throw e;
    }
  }
/**
 * @param {?} throwOnChange
 * @return {?}
 */
detectChanges(throwOnChange: boolean): void {
    this._resetDebug();
    try {
      super.detectChanges(throwOnChange);
    } catch ( /** @type {?} */e) {
      this._rethrowWithContext(e);
      throw e;
    }
  }
/**
 * @return {?}
 */
private _resetDebug() { this._currentDebugContext = null; }
/**
 * @param {?} nodeIndex
 * @param {?} rowNum
 * @param {?} colNum
 * @return {?}
 */
debug(nodeIndex: number, rowNum: number, colNum: number): DebugContext {
    return this._currentDebugContext = new DebugContext(this, nodeIndex, rowNum, colNum);
  }
/**
 * @param {?} e
 * @return {?}
 */
private _rethrowWithContext(e: any) {
    if (!(e instanceof ViewWrappedError)) {
      if (!(e instanceof ExpressionChangedAfterItHasBeenCheckedError)) {
        this.cdMode = ChangeDetectorStatus.Errored;
      }
      if (isPresent(this._currentDebugContext)) {
        throw new ViewWrappedError(e, this._currentDebugContext);
      }
    }
  }
/**
 * @param {?} cb
 * @return {?}
 */
eventHandler<E, R>(cb: (eventName: string, event?: E) => R): (eventName: string, event?: E) => R {
    const /** @type {?} */ superHandler = super.eventHandler(cb);
    return (eventName: string, event?: any) => {
      this._resetDebug();
      try {
        return superHandler.call(this, eventName, event);
      } catch ( /** @type {?} */e) {
        this._rethrowWithContext(e);
        throw e;
      }
    };
  }
}

function DebugAppView_tsickle_Closure_declarations() {
 /** @type {?} */
DebugAppView.prototype._currentDebugContext;
 /** @type {?} */
DebugAppView.prototype.staticNodeDebugInfos;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
